/* =========================================================================
   Skippable break pre-card (PLAN-gameplay-v2 §3 — "always skippable").

   When the Moment Director grants a mini-game break, the player sees this small
   card FIRST: what the break is, a primary "Play", and a quiet "Skip →" that
   fades in after a short beat (so a speedrunner passes and a lover stays,
   without anyone skipping by reflex on the first frame).

   Eric's rule: skipping forfeits NOTHING. The pre-card's skip banks exactly the
   same graceful pass the mini-games' own "Skip this break →" links bank, and
   the break clock resets either way — having the pause offered counts as having
   had it (see momentDirector.applyGrant).

   This module WRAPS the mini-games; it never touches them. Pure label logic is
   exported for tests, the DOM is built on demand like roundPicker.ts.
   ========================================================================= */
import type { BreakType } from './data/packs';
import { prefersReducedMotion } from './ui/momentOverlay';

/** Beat before "Skip →" appears — long enough that nobody skips by reflex. */
export const SKIP_BEAT_MS = 1_000;

/** The graceful pass, in cents. Deliberately the SAME $50 the mini-games award
    on their own skip links (manna.ts / match3.ts / maryMatch3.ts SKIP_AWARD) —
    duplicated rather than imported so this wrapper never reaches into them. */
export const BREAK_SKIP_AWARD = 5_000;

export type BreakChoice = 'play' | 'skip';

/** Display name for a break type. PURE. */
export function breakCardTitle(type: BreakType): string {
  switch (type) {
    case 'manna':
      return 'Manna from Heaven';
    case 'match3':
      return 'Faith Match';
    case 'marymatch3':
      return 'Mother Mary Match-3';
    default:
      return 'Bonus Wheel';
  }
}

/** One warm line under the title. PURE. */
export function breakCardBlurb(type: BreakType): string {
  switch (type) {
    case 'manna':
      return 'Catch the falling manna — a minute of desert calm.';
    case 'match3':
      return 'Clear the board and rest your mind a moment.';
    case 'marymatch3':
      return 'Three gentle tiers with Our Lady.';
    default:
      return 'One spin, and providence decides.';
  }
}

const OVERLAY_ID = 'breakCardOv';

/**
 * Show the pre-card and resolve with the player's choice. Safe with no DOM
 * (resolves 'play', i.e. exactly today's behaviour). Escape = skip.
 */
export function showBreakCard(type: BreakType): Promise<BreakChoice> {
  if (typeof document === 'undefined') return Promise.resolve('play');

  const root = document.createElement('div');
  root.id = OVERLAY_ID;
  root.className = 'bc-ov';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', 'Break time');

  root.innerHTML =
    `<div class="bc-card">` +
    `<div class="bc-kicker">Break time</div>` +
    `<div class="bc-title">${breakCardTitle(type)}</div>` +
    `<div class="bc-blurb">${breakCardBlurb(type)}</div>` +
    `<button type="button" class="btn bc-play" id="bcPlay">Play</button>` +
    `<button type="button" class="bc-skip" id="bcSkip">Skip →</button>` +
    `</div>`;

  const prev = document.getElementById(OVERLAY_ID);
  if (prev && prev.parentNode) prev.parentNode.removeChild(prev);
  const host = (document.querySelector('.screen') as HTMLElement | null) || document.body;
  host.appendChild(root);

  return new Promise<BreakChoice>((resolve) => {
    let settled = false;

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish('skip');
      }
    }

    function finish(choice: BreakChoice): void {
      if (settled) return;
      settled = true;
      clearTimeout(beat);
      document.removeEventListener('keydown', onKey, true);
      root.classList.add('mo-out');
      const remove = (): void => {
        if (root.parentNode) root.parentNode.removeChild(root);
      };
      if (prefersReducedMotion()) remove();
      else setTimeout(remove, 200);
      resolve(choice);
    }

    // "Skip →" fades in after the beat (reduced motion: immediately, still late).
    const beat = setTimeout(() => root.classList.add('bc-skippable'), SKIP_BEAT_MS);

    const play = root.querySelector<HTMLButtonElement>('#bcPlay');
    if (play) {
      play.addEventListener('click', () => finish('play'));
      try {
        play.focus();
      } catch {
        /* focus can throw in odd embeds — tapping still works */
      }
    }
    const skip = root.querySelector<HTMLButtonElement>('#bcSkip');
    if (skip) skip.addEventListener('click', () => finish('skip'));
    document.addEventListener('keydown', onKey, true);
  });
}
